import { useEffect, useState } from 'react';
import { ApolloError } from '@apollo/client';

import {
  DevHubCircularProgress,
  DevHubDynamicIcon,
  DevHubTooltip,
  devhubMakeStyles,
} from '@devxdiscover/devhub-ui';
import { MutationStatus } from './types/updateApplication/BaseApplicationProperty';

type AppBulkUpdateStatusProps = {
  applicationIds: number[];
  applicationId: number;
  toggleSubmit: boolean;
  mutationStatus?: MutationStatus[];
  loading: boolean;
  error?: ApolloError;
};

type status = 'loading' | 'success' | 'error';

const useStyles = devhubMakeStyles(() => ({
  container: {
    display: 'flex',
    alignItems: 'center',
  },
}));

/**
 * Component to display the bulk update status of applications.
 *
 * @param props - The properties for the component.
 * @returns The JSX element for the bulk update status.
 */
const AppBulkUpdateStatus = (props: AppBulkUpdateStatusProps): JSX.Element => {
  const applicationId = props.applicationId;
  const [status, setStatus] = useState<status>('loading');
  const [toolTipMessage, setToolTipMessage] = useState<string>('');
  const classes = useStyles();

  useEffect(() => {
    if (!props.loading) {
      if (props?.mutationStatus && props.mutationStatus.length > 0) {
        const currentStatus = props.mutationStatus?.find(
          appStatus => appStatus.applicationId === applicationId,
        );
        if (currentStatus) {
          setToolTipMessage(currentStatus.message);
          setStatus(currentStatus.success ? 'success' : 'error');
        }
      } else if (props?.error) {
        setStatus('error');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.loading, props?.error, props?.mutationStatus]);

  return (
    <>
      <DevHubTooltip title={`${toolTipMessage}`}>
        <span>
          {status === 'error' && (
            <>
              <div className={classes.container}>
                <DevHubDynamicIcon iconName="Error" color="error" />
                <span>Error</span>
              </div>
            </>
          )}
          {status === 'success' && (
            <>
              <div className={classes.container}>
                <DevHubDynamicIcon iconName="CheckCircle" color="success" />
                <span>Success</span>
              </div>
            </>
          )}
        </span>
      </DevHubTooltip>
      {status !== 'success' && status !== 'error' && (
        <>
          <DevHubCircularProgress /> Pending
        </>
      )}
    </>
  );
};

export default AppBulkUpdateStatus;
